import React, { FC, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';

import { ChevronDown } from 'lucide-react';

import AdBanner from '../ad-banner';
import {
  FEATURE_FLAG_MENU_NOTIFICATIONS,
  FEATURE_FLAG_PLANS_V2,
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../constants/featureFlags';

import { useSupportTicketUnreadCount } from '../../features/support/use-support-ticket-unread-count';
import { useSupportAiBotFeatureFlag } from '../../features/support/useSupportAiBotFeatureFlag';
import { IconButton } from '../../shared/ui';

import { MenuBellTrailing, MenuNotificationTrailing, MenuUnreadCountTrailing } from './menu-notification-trailing';
import { getNotificationsForMenuItem, parseMenuNotificationsPayload } from './menu-notification-data';

const itemBase =
  'w-full flex items-center justify-between gap-2 px-4 py-3 rounded-control text-ui-s-strong leading-snug transition-colors text-left';

const itemInactive = 'bg-surface-muted text-ink-muted hover:bg-line';
const itemActive = 'bg-primary text-white';

interface DesktopMenuProps {
  path: string;
}

export const DesktopMenu: FC<DesktopMenuProps> = ({ path }) => {
  const [isToolsOpen, setIsToolsOpen] = useState(path.includes('tools'));
  const isToolsRoute = path.includes('tools');
  const menuNotificationsPayload = useFeatureFlagPayload(FEATURE_FLAG_MENU_NOTIFICATIONS);
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const isPlansV2Enabled = useFeatureFlagEnabled(FEATURE_FLAG_PLANS_V2);
  const { enabled: isSupportAiBotEnabled } = useSupportAiBotFeatureFlag();
  const unreadSupportMessages = useSupportTicketUnreadCount(isSupportAiBotEnabled);

  useEffect(() => {
    if (isToolsRoute) {
      setIsToolsOpen(true);
    } else {
      setIsToolsOpen(false);
    }
  }, [isToolsRoute]);

  const toolsIsHighlighted = isToolsOpen;

  const menuNotifications = parseMenuNotificationsPayload(menuNotificationsPayload);
  const subLinkClass = ({ isActive }: { isActive: boolean }) =>
    classNames(
      'w-full rounded-control px-3 py-2 text-ui-s-strong leading-snug transition-colors text-left',
      // Two disjoint states: an active link must not carry the inactive hover, or hover turns it grey under white text.
      isActive ? itemActive : 'text-ink-muted hover:bg-line/80',
    );

  const toolsNotifications = getNotificationsForMenuItem('tools', menuNotifications);
  const tariffsNotifications = getNotificationsForMenuItem('tariffs', menuNotifications);
  const infoNotifications = getNotificationsForMenuItem('info', menuNotifications);

  return (
    <aside className="hidden lg:sticky lg:top-[90px] lg:flex lg:h-[calc(100vh-100px)] lg:flex-col lg:shrink-0 w-80 min-w-80 min-h-0 overflow-y-auto px-4 gap-2 bg-white mb-2.5 pt-[24px]">
      <NavLink to="/profile" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        <span className="min-w-0 flex-1 text-left">Профиль</span>
      </NavLink>
      <NavLink
        to="/consultations"
        className={({ isActive }) => classNames(itemBase, isActive || path === 'settings' ? itemActive : itemInactive)}
      >
        <span className="min-w-0 flex-1 text-left">Создать встречу</span>
      </NavLink>
      <NavLink to="/journal" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        <span className="min-w-0 flex-1 text-left">Терапевтический журнал</span>
      </NavLink>
      <div className="relative w-full min-w-0 max-w-full">
        <NavLink
          to="/tools"
          className={({ isActive }) =>
            classNames(
              itemBase,
              'pr-24',
              toolsIsHighlighted || isActive ? itemActive : itemInactive,
              isToolsOpen && 'rounded-b-none',
            )
          }
        >
          <span className="min-w-0 flex-1 text-left">Инструменты</span>
        </NavLink>
        <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2 whitespace-nowrap">
          <MenuBellTrailing notifications={toolsNotifications} isActive={toolsIsHighlighted} />
          <IconButton
            label="Переключить список инструментов"
            variant={toolsIsHighlighted ? 'ghostInverse' : 'ghost'}
            className="-m-1.5"
            aria-expanded={isToolsOpen}
            onClick={() => setIsToolsOpen((prev) => !prev)}
          >
            <ChevronDown
              size={16}
              className={classNames(
                'transition-transform',
                toolsIsHighlighted ? 'text-white' : 'text-ink-subtle',
                isToolsOpen ? 'rotate-180' : 'rotate-0',
              )}
            />
          </IconButton>
        </div>
      </div>

      <div
        className={classNames(
          'w-full min-w-0 max-w-full shrink-0 self-stretch overflow-hidden flex-col gap-1.5 rounded-b-control bg-surface-muted px-3 py-3 -mt-4',
          isToolsOpen ? 'flex' : 'hidden',
        )}
      >
        <NavLink to="/tools/ai-assistants" className={subLinkClass}>
          ИИ Ассистенты
        </NavLink>
        <NavLink to="/tools/emotion-detector" className={subLinkClass}>
          Детектор эмоций
        </NavLink>
        <NavLink to="/tools/metaphorical-cards" className={subLinkClass}>
          Метафорические карты
        </NavLink>
        <NavLink to="/tools/question" className={subLinkClass}>
          Подкинь вопрос
        </NavLink>
        <NavLink to="/tools/dpdg" className={subLinkClass}>
          ДПДГ онлайн
        </NavLink>
        <NavLink to="/tools/life-balance-wheel" className={subLinkClass}>
          Колесо баланса
        </NavLink>
        <NavLink to="/tools/ai-grow" className={subLinkClass}>
          GROW AI
        </NavLink>
        {isColorCodeEnabled && (
          <NavLink to="/tools/color-code" className={subLinkClass}>
            Цветовой код
          </NavLink>
        )}
        {isCloudsEnabled && (
          <NavLink to="/tools/clouds" className={subLinkClass}>
            Облака
          </NavLink>
        )}
        {isSandTherapyEnabled && (
          <NavLink to="/tools/sand-therapy" className={subLinkClass}>
            Песочная терапия
          </NavLink>
        )}
        {isSandTherapyEnabled && (
          <NavLink to="/tools/arrangements" className={subLinkClass}>
            Расстановки
          </NavLink>
        )}
      </div>

      {isPlansV2Enabled ? (
        <NavLink
          to="/subscriptions"
          className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}
        >
          <span className="min-w-0 flex-1 text-left">Мои подписки</span>
        </NavLink>
      ) : (
        <NavLink to="/tariffs" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
          {({ isActive }) => (
            <>
              <span className="min-w-0 flex-1 text-left">Тарифы и бонусы</span>
              <MenuNotificationTrailing notifications={tariffsNotifications} isActive={isActive} />
            </>
          )}
        </NavLink>
      )}
      <NavLink to="/info" className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}>
        {({ isActive }) => (
          <>
            <span className="min-w-0 flex-1 text-left">Информация</span>
            <MenuNotificationTrailing notifications={infoNotifications} isActive={isActive} />
          </>
        )}
      </NavLink>
      <NavLink
        to="/referal-management"
        className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}
      >
        <span className="min-w-0 flex-1 text-left">Ваш ключ</span>
      </NavLink>

      {isSupportAiBotEnabled ? (
        <NavLink
          to="/support-tickets"
          className={({ isActive }) => classNames(itemBase, isActive ? itemActive : itemInactive)}
        >
          {({ isActive }) => (
            <>
              <span className="min-w-0 flex-1 text-left">Мои обращения</span>
              <MenuUnreadCountTrailing count={unreadSupportMessages} isActive={isActive} />
            </>
          )}
        </NavLink>
      ) : null}
      <div className="mt-2">
        <AdBanner />
      </div>
    </aside>
  );
};

export default DesktopMenu;
